import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  UsersThree, ArrowLeft, MapPin, GameController, Clock, CalendarDots,
  UserPlus, SignOut, Megaphone, Star, Eye, Users, Plus
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const FREQ_LABEL = { weekly: 'Weekly', biweekly: 'Bi-weekly', monthly: 'Monthly' };

export default function RecurringGroupDetailPage() {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);

  const fetchGroup = async () => {
    try {
      const res = await axios.get(`${API}/api/recurring-groups/${groupId}`, { withCredentials: true });
      setGroup(res.data);
    } catch (e) {
      if (e.response?.status === 403) {
        toast.error('This group is private');
        navigate('/groups/recurring');
      }
    }
    setLoading(false);
  };

  useEffect(() => { fetchGroup(); }, [groupId]);

  const isMember = group?.members?.some(m => m.user_id === user?.id);
  const isOwner = group?.owner_user_id === user?.id;

  const handleJoin = async () => {
    setJoining(true);
    try {
      await axios.post(`${API}/api/recurring-groups/${groupId}/join`, {}, { withCredentials: true });
      toast.success('Joined group!');
      fetchGroup();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setJoining(false);
  };

  const handleLeave = async () => {
    try {
      await axios.post(`${API}/api/recurring-groups/${groupId}/leave`, {}, { withCredentials: true });
      toast.success('Left group');
      navigate('/groups/recurring');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleGenerate = async () => {
    try {
      const res = await axios.post(`${API}/api/recurring-groups/${groupId}/generate-events`, { count: 4 }, { withCredentials: true });
      toast.success(`Generated ${res.data.generated} events`);
      fetchGroup();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  if (loading) return <div className="max-w-3xl mx-auto"><div className="h-40 bg-white/[0.03] rounded-lg animate-pulse" /></div>;
  if (!group) return <p className="text-[#444] text-center mt-10">Group not found</p>;

  const rule = group.recurring_rule;

  return (
    <div className="max-w-3xl mx-auto" data-testid="group-detail-page">
      <button onClick={() => navigate('/groups/recurring')} className="flex items-center gap-1 text-[12px] text-[#555] hover:text-white mb-4 transition-colors">
        <ArrowLeft size={14} /> Back to Groups
      </button>

      {/* Header */}
      <div className="bg-[#0c0c14] border border-white/[0.06] rounded-xl p-5 mb-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-[#9D4CDD]/10 flex items-center justify-center">
              <UsersThree size={24} weight="duotone" className="text-[#9D4CDD]" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">{group.name}</h1>
              <div className="flex items-center gap-2 mt-0.5 text-[12px] text-[#555]">
                {group.city && <span className="flex items-center gap-1"><MapPin size={12} /> {group.city}{group.state ? `, ${group.state}` : ''}</span>}
                {group.primary_game_id && <><span className="text-[#222]">|</span><span className="flex items-center gap-1"><GameController size={12} /> {group.primary_game_id}</span></>}
                <span className="text-[#222]">|</span>
                <Users size={12} /> {group.member_count} members
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            {!isMember && (
              <Button size="sm" onClick={handleJoin} disabled={joining}
                className="h-8 text-[11px] bg-[#9D4CDD] hover:bg-[#8B3DC9] text-white" data-testid="join-group-btn">
                <UserPlus size={13} className="mr-1" /> {joining ? 'Joining...' : 'Join Group'}
              </Button>
            )}
            {isMember && !isOwner && (
              <Button size="sm" variant="outline" onClick={handleLeave}
                className="h-8 text-[11px] bg-transparent border-[#EF4444]/30 text-[#EF4444] hover:bg-[#EF4444]/10" data-testid="leave-group-btn">
                <SignOut size={13} className="mr-1" /> Leave
              </Button>
            )}
            {isOwner && (
              <Button size="sm" onClick={handleGenerate}
                className="h-8 text-[11px] bg-[#10B981] hover:bg-[#059669] text-white" data-testid="generate-events-btn">
                <Plus size={13} className="mr-1" /> Generate Events
              </Button>
            )}
          </div>
        </div>
        {group.description && <p className="text-[12px] text-[#666] mt-3">{group.description}</p>}

        <div className="flex flex-wrap gap-2 mt-3">
          <span className="text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full border border-[#9D4CDD]/20 text-[#9D4CDD] bg-[#9D4CDD]/5 font-medium">
            {group.group_type?.replace(/_/g, ' ')}
          </span>
          <span className="text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full border border-white/[0.1] text-[#888] bg-white/[0.02] font-medium">
            <Eye size={10} className="inline mr-0.5" /> {group.visibility}
          </span>
          {group.beginner_friendly && (
            <span className="text-[9px] bg-[#10B981]/10 text-[#10B981] px-2 py-0.5 rounded-full border border-[#10B981]/20">
              Beginner Friendly
            </span>
          )}
          <span className="text-[9px] px-2 py-0.5 rounded-full border border-white/[0.1] text-[#666] bg-white/[0.02]">
            {group.competitive_level}
          </span>
        </div>
      </div>

      {/* Schedule */}
      {rule && (
        <div className="bg-[#0c0c14] border border-white/[0.06] rounded-xl p-5 mb-4">
          <h2 className="text-[14px] font-bold text-white mb-3 flex items-center gap-2">
            <Clock size={16} weight="duotone" className="text-[#F59E0B]" /> Schedule
          </h2>
          <div className="flex items-center gap-4 text-[13px] text-[#888]">
            <span className="text-white font-medium">{FREQ_LABEL[rule.frequency] || rule.frequency}</span>
            <span>{DAYS[rule.day_of_week]}s at {rule.time}</span>
            <span>{rule.duration_minutes} min</span>
          </div>
        </div>
      )}

      {/* Upcoming Events */}
      <div className="bg-[#0c0c14] border border-white/[0.06] rounded-xl p-5 mb-4">
        <h2 className="text-[14px] font-bold text-white mb-3 flex items-center gap-2">
          <CalendarDots size={16} weight="duotone" className="text-[#3B82F6]" /> Upcoming Events
        </h2>
        {group.upcoming_events?.length > 0 ? (
          <div className="space-y-2">
            {group.upcoming_events.map(e => (
              <div key={e.id} className="flex items-center justify-between px-3 py-2.5 rounded-md bg-white/[0.02] border border-white/[0.04]"
                data-testid={`event-${e.id}`}>
                <div>
                  <p className="text-[13px] text-white font-medium">{e.event_title}</p>
                  <p className="text-[11px] text-[#555]">{new Date(e.scheduled_start).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                </div>
                <span className={`text-[9px] uppercase px-2 py-0.5 rounded-full font-medium ${
                  e.status === 'scheduled' ? 'bg-[#3B82F6]/10 text-[#3B82F6] border border-[#3B82F6]/20' :
                  e.status === 'active' ? 'bg-[#10B981]/10 text-[#10B981] border border-[#10B981]/20' :
                  'bg-white/5 text-[#555] border border-white/10'
                }`}>{e.status}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-[12px] text-[#444] text-center py-3">No upcoming events {isOwner ? '— generate some!' : ''}</p>
        )}
      </div>

      {/* Announcements */}
      {group.announcements?.length > 0 && (
        <div className="bg-[#0c0c14] border border-white/[0.06] rounded-xl p-5 mb-4">
          <h2 className="text-[14px] font-bold text-white mb-3 flex items-center gap-2">
            <Megaphone size={16} weight="duotone" className="text-[#EC4899]" /> Announcements
          </h2>
          <div className="space-y-3">
            {group.announcements.map(a => (
              <div key={a.id} className="px-3 py-2.5 rounded-md bg-white/[0.02] border border-white/[0.04]"
                data-testid={`announcement-${a.id}`}>
                <div className="flex items-center justify-between mb-1">
                  <h3 className="text-[13px] text-white font-semibold">{a.title}</h3>
                  {a.pinned && <Star size={12} weight="fill" className="text-[#F59E0B]" />}
                </div>
                <p className="text-[12px] text-[#666]">{a.body}</p>
                <p className="text-[10px] text-[#444] mt-1">by {a.author_name} — {new Date(a.created_at).toLocaleDateString()}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Members */}
      <div className="bg-[#0c0c14] border border-white/[0.06] rounded-xl p-5">
        <h2 className="text-[14px] font-bold text-white mb-3 flex items-center gap-2">
          <Users size={16} weight="duotone" className="text-[#9D4CDD]" /> Members ({group.members?.length})
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {group.members?.map(m => (
            <div key={m.user_id} className="flex items-center gap-2 px-3 py-2 rounded-md bg-white/[0.02] border border-white/[0.04] cursor-pointer hover:border-white/[0.1] transition-colors"
              onClick={() => navigate(`/user/${m.user_id}`)} data-testid={`member-${m.user_id}`}>
              <div className="w-7 h-7 rounded-full bg-[#9D4CDD]/10 flex items-center justify-center text-[10px] text-[#9D4CDD] font-bold">
                {m.name?.charAt(0) || '?'}
              </div>
              <div className="min-w-0">
                <p className="text-[12px] text-white font-medium truncate">{m.name}</p>
                <p className="text-[10px] text-[#555]">Lv.{m.level}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
